import { useEffect, useState } from "react";
import { brand } from "../siteContent";
import { NavLink } from "./NavLink";
import { StoreDownloadActions } from "./StoreDownloadActions";

const FEATURED_VIDEO = {
  id: "fxVnVrUzDaY",
  url: "https://youtube.com/shorts/fxVnVrUzDaY?feature=share"
};

function FeaturedVideo({ videoLoaded, onPlay }) {
  return (
    <div className="hero-featured-video">
      <div className="hero-video-reel-heading">
        <div>
          <small>Featured video</small>
          <strong>Watch Aspirant CSS</strong>
        </div>
        <span>Play directly on this page</span>
        <div className="hero-video-value" aria-label="Aspirant CSS app highlights">
          <small>Prepare in one place</small>
          <div className="hero-video-message" aria-hidden="true">
            <strong style={{ "--message-index": 0 }}>Understand the CSS syllabus.</strong>
            <strong style={{ "--message-index": 1 }}>Practice solved past papers.</strong>
            <strong style={{ "--message-index": 2 }}>Test yourself with timed MCQs.</strong>
            <strong style={{ "--message-index": 3 }}>Continue from your saved progress.</strong>
          </div>
          <span className="hero-video-message-accessible">
            Understand the syllabus, study solved papers, practice timed MCQs and save your progress.
          </span>
          <div className="hero-video-feature-chips" aria-hidden="true">
            <span>Lessons</span>
            <span>Solved papers</span>
            <span>MCQs</span>
          </div>
        </div>
      </div>
      <div className="hero-video-reel-frame">
        {videoLoaded ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${FEATURED_VIDEO.id}?rel=0&playsinline=1&autoplay=1`}
            title="Aspirant CSS featured YouTube Short"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            className="hero-video-load-button"
            onClick={onPlay}
            aria-label="Play the Aspirant CSS featured video"
          >
            <img
              src={brand.cover}
              alt=""
              width="1672"
              height="941"
              loading="lazy"
              decoding="async"
            />
            <span className="hero-video-play" aria-hidden="true">▶</span>
            <strong>Play video</strong>
          </button>
        )}
      </div>
      <a href={FEATURED_VIDEO.url} target="_blank" rel="noreferrer" className="hero-video-youtube-link">
        Open on YouTube <span aria-hidden="true">↗</span>
      </a>
    </div>
  );
}

function HomeCover({ navigate }) {
  return (
    <NavLink
      to="/aspirant-app"
      className="home-hero-cover"
      onNavigate={navigate}
      aria-label="Explore the Aspirant CSS mobile app"
    >
      <img
        src={brand.cover}
        alt="Aspirant CSS, MPT and PMS smart preparation mobile app"
        width="1672"
        height="941"
        decoding="async"
        fetchPriority="high"
      />
    </NavLink>
  );
}

export function Hero({ page, navigate }) {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [stackedLayout, setStackedLayout] = useState(() =>
    typeof window !== "undefined" && window.matchMedia?.("(max-width: 980px)").matches
  );
  const animatedWords = page.hero?.words || [];

  useEffect(() => {
    const media = window.matchMedia?.("(max-width: 980px)");
    if (!media) return undefined;
    const updateLayout = () => setStackedLayout(media.matches);
    if (media.addEventListener) {
      media.addEventListener("change", updateLayout);
    } else {
      media.addListener(updateLayout);
    }
    updateLayout();
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener("change", updateLayout);
      } else {
        media.removeListener(updateLayout);
      }
    };
  }, []);

  return (
    <section className="hero office-hero">
      <div className="hero-copy">
        <span className="eyebrow">{page.hero?.eyebrow || "Aspirant CSS"}</span>
        <h1>{page.hero?.headline || page.title}</h1>
        {page.kind === "home" ? (
          <>
            <StoreDownloadActions className="hero-download-actions-desktop" />
            {stackedLayout ? <HomeCover navigate={navigate} /> : <FeaturedVideo videoLoaded={videoLoaded} onPlay={() => setVideoLoaded(true)} />}
          </>
        ) : null}
        {page.kind !== "home" && animatedWords.length ? (
          <div className="hero-wordline" aria-label={`Start with ${animatedWords.join(", ")}`}>
            <span>Start with:</span>
            <strong>{animatedWords.join(" · ")}</strong>
          </div>
        ) : null}
        {page.kind !== "home" ? <p className="hero-summary">{page.hero?.summary || page.description}</p> : null}
        {page.kind !== "home" && page.hero?.chips?.length ? (
          <div className="chip-row" aria-label="Key topics">
            {page.hero.chips.map((chip) => (
              <span key={chip} className="chip">
                {chip}
              </span>
            ))}
          </div>
        ) : null}
        {page.kind !== "home" && !page.hero?.hideActions ? (
          <div className="hero-actions">
            <NavLink to="/css-2027" className="button button-primary" onNavigate={navigate}>
              Start CSS from zero
            </NavLink>
            <NavLink to="/css-subjects" className="button button-secondary" onNavigate={navigate}>
              See subject map
            </NavLink>
          </div>
        ) : null}
      </div>
      {page.kind === "home" ? (
        <div className="hero-mobile-download">
          <h2>CSS MPT App</h2>
          <StoreDownloadActions />
        </div>
      ) : null}
      <aside className={`hero-panel study-room-panel${page.kind === "home" ? " home-hero-panel" : ""}`}>
        {page.kind === "home" ? (
          stackedLayout ? <FeaturedVideo videoLoaded={videoLoaded} onPlay={() => setVideoLoaded(true)} /> : <HomeCover navigate={navigate} />
        ) : (
          <>
            <div className="hero-panel-copy">
              <span className="section-eyebrow">At a glance</span>
              <p className="hero-panel-title">{page.title}</p>
            </div>
          </>
        )}
        {page.kind === "home" && page.stats?.length ? (
          <div className="home-hero-journey" aria-label="Aspirant CSS mobile app features">
            <div className="home-journey-heading">
              <span>Inside the app</span>
              <strong>Everything your preparation needs</strong>
            </div>
            <div className="home-journey-message" aria-hidden="true">
              {page.stats.map((item, index) => (
                <strong key={`${item.label}-${item.value}`} style={{ "--journey-index": index }}>
                  <small>{item.label}</small>
                  {item.value}
                </strong>
              ))}
            </div>
            <span className="home-journey-accessible">
              Study structured subjects, read solved papers, practice MCQs and mocks, and revise through study reels.
            </span>
            <div className="home-journey-steps" aria-hidden="true">
              {page.stats.map((item, index) => (
                <span key={`${item.label}-step`}>
                  <b>{index + 1}</b>
                  {item.value}
                </span>
              ))}
            </div>
          </div>
        ) : page.stats?.length ? (
          <div className="metric-grid">
            {page.stats.map((item) => (
              <div key={`${item.label}-${item.value}`} className="metric-card">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
        ) : null}
      </aside>
    </section>
  );
}
